import React, { useState, useEffect } from 'react';
import { TextField, Button, CircularProgress, Stack, Typography, MenuItem, FormControl, InputLabel, Select, Card, CardContent, CardHeader } from '@mui/material';
import { useContent } from '../../../../context/ContentContext';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'; // Import DemoContainer
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'; // Import AdapterDayjs
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'; // Import LocalizationProvider
import { DatePicker } from '@mui/x-date-pickers/DatePicker'; // Import DatePicker
import dayjs from 'dayjs'; // Import dayjs

const BookingOrderDisplay = () => {
    const { categories, loading, userData, addresses } = useContent();
    const [nameAffiliate] = useState(userData?.name || '');
    const [referralIdAffiliate] = useState(userData?.referral_id || '');
    const [mounted, setMounted] = useState(false);

    const BranchesDropdown = ({ branches, selectedBranch, onSelect }) => {
        return (
            <FormControl fullWidth>
                <InputLabel id="branch-select-label">Cabang</InputLabel>
                <Select
                    labelId="branch-select-label"
                    id="branch-select"
                    value={selectedBranch || ''} // Default to an empty string
                    onChange={(e) => onSelect(e.target.value)} // Call the parent updater
                >
                    {/* Placeholder item for the default state */}
                    <MenuItem value="" disabled>
                        Pilih Workshop
                    </MenuItem>
                    {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.name}>
                            {branch.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    const ServiceDropdown = ({ onSelect }) => {
        return (
            <FormControl fullWidth>
                <InputLabel id="service-select-label">Layanan</InputLabel>
                <Select
                    labelId="service-select-label"
                    id="service-select"
                    onChange={(e) => onSelect(e.target.value)}
                >
                    <MenuItem value="" disabled>
                        Pilih Layanan
                    </MenuItem>
                    <MenuItem value="Pick Up">Pick Up</MenuItem>
                    <MenuItem value="Visit Workshop">Visit Workshop</MenuItem>
                    <MenuItem value="Home Service">Home Service</MenuItem>
                </Select>
            </FormControl>
        );
    };

    const WhatsAppBooking = ({ branches }) => {
        const [name, setName] = useState('');
        const [email, setEmail] = useState('');
        const [phone, setPhone] = useState('');
        const [carModel, setCarModel] = useState('');
        const [selectedBranch, setSelectedBranch] = useState('');
        const [service, setService] = useState('');
        const [date, setDate] = useState(dayjs());
        const [message, setMessage] = useState('');

        const handleSubmit = () => {
            // Check if essential fields are filled in
            if (!name || !email || !phone || !carModel || !selectedBranch || !service || !date.isValid()) {
                alert('Please fill in all fields and select a valid date.');
                return; // Prevent submission if validation fails
            }

            const bookingDate = date ? date.format('DD/MM/YYYY') : ''; // Format for submission
            const whatsappMessage = `
            Halo, saya *${nameAffiliate}* *${referralIdAffiliate}*. Mau booking order untuk konsumen saya.\n
            Nama: ${name}
            Email: ${email}
            Telepon: ${phone}
            Model Mobil: ${carModel}
            Cabang: ${selectedBranch}
            Order Type: ${service}
            Booking Date: ${bookingDate}\n            
            Pesan:
            ${message}\n
            Tolong segera diproses, terima kasih.`;

            const whatsappUrl = `https://wa.me/6281297974303?text=${encodeURIComponent(whatsappMessage)}`;
            window.open(whatsappUrl, '_blank');
        };

        useEffect(() => {
            if (!mounted) {
                setMounted(true);
            }
        }, [mounted]);

        if (loading || !mounted) {
            return <CircularProgress />;
        }

        if (!categories || categories.length === 0) {
            return (
                <Stack sx={{ width: '100%', height: '100vh', justifyContent: 'center', alignItems: 'center' }} direction="column" spacing={2}>
                    <Typography variant="h6">Tidak ada Form Order</Typography>
                    <img
                        src="/images/NoResultFound.webp"
                        alt="No categories found"
                        style={{ width: '300px', height: 'auto' }}
                    />
                </Stack>
            );
        }

        const handleDateChange = (newDate) => {
            if (newDate && newDate.isValid()) {
                setDate(newDate);
            } else {
                setDate(dayjs()); // Fall back to current date if invalid
            }
        };

        return (
            <Stack
                spacing={3}
                sx={{
                    p: 0,
                    width: {
                        xs: '100vw',  // On extra small devices (mobile)
                        sm: '100vw',  // On small devices (tablet)
                        md: '100vw',  // On medium devices
                        lg: '80vw',  // On large devices
                    },
                    mx: 'auto', // Center the stack horizontally
                }}
            >
                <Card sx={{ maxWidth: '90vw', mx: 'auto', boxShadow: 3, borderRadius: 2 }}>
                    <CardHeader title="Booking Form" />
                    <CardContent>
                        <Stack spacing={1}>
                            <TextField label="Nama" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
                            <TextField label="Email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} />
                            <TextField label="Telepon" fullWidth value={phone} onChange={(e) => setPhone(e.target.value)} />
                            <TextField label="Model Mobil" fullWidth value={carModel} onChange={(e) => setCarModel(e.target.value)} />
                            <BranchesDropdown
                                branches={branches}
                                selectedBranch={selectedBranch} // Pass the selected branch value
                                onSelect={setSelectedBranch} // Pass the state updater function
                            />
                            <ServiceDropdown onSelect={setService} />

                            {/* Use MUI DatePicker here with AdapterDayjs */}
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Booking Date"
                                    value={(date)} // Ensure the value is always a valid dayjs object
                                    onChange={handleDateChange}
                                    format='DD/MM/YYYY'
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <TextField
                                label="Referral ID"
                                fullWidth
                                value={referralIdAffiliate} // Use referralIdAffiliate directly from userData
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                    }
                                }}
                            />
                            <TextField
                                label="Pesan"
                                fullWidth
                                multiline
                                rows={Infinity}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                sx={{
                                    '& .MuiInputBase-inputMultiline': {
                                        minHeight: '100px', // Set min height for the textarea
                                    },
                                }}
                            />
                        </Stack>
                        <Button variant="contained" color="primary" onClick={handleSubmit} fullWidth sx={{ mt: 2 }}>
                            Send WhatsApp
                        </Button>
                    </CardContent>
                </Card>
            </Stack>);
    };

    return <WhatsAppBooking branches={addresses} />;
};

export default BookingOrderDisplay;
